/**
 * KpiCard - one Dashboard KPI box (Total Pipeline, Open Opportunities and the
 * week boxes). It fills the widget it sits in (the customizable grid sizes
 * it); the figure stays at the top and the date line at the bottom.
 *
 * A box with `onOpen` is a button: click, Enter or Space opens its window
 * (the week boxes). `muted` greys a value that is not a figure;
 * `explainAlign: 'right'` opens the "?" leftwards (a right-hand box);
 * `footer` sits at the end of the date line (the week boxes' This / Last
 * switch). Moved out of DashboardView 10/12/26.
 *
 * Its sizes live in index.css (.kpi-card*) so a narrow box (3 of 12 columns)
 * tightens its padding and figure (a container query on the widget). Hover
 * only deepens the shadow: a transform made the box its own layer and the
 * "?" card went under the boxes beside it.
 */
import { Icon } from '../ui/Icon';
import { Explainer } from '../ui/Explainer';

export default function KpiCard({ kpi }) {
  const opens = typeof kpi.onOpen === 'function';
  return (
    <div
      title={kpi.title}
      {...(opens
        ? {
            role: 'button',
            tabIndex: 0,
            'aria-label': `${kpi.label}: open the breakdown`,
            onClick: kpi.onOpen,
            // only the box's own key press: Enter on its "?" button opens the explainer
            onKeyDown: (e) => {
              if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                kpi.onOpen();
              }
            },
          }
        : null)}
      className={`kpi-card${opens ? ' kpi-card-opens go-focus' : ''}`}
      style={kpi.bg ? { background: kpi.bg } : undefined}
    >
      <div className="kpi-card-head">
        <span className="kpi-card-label" style={{ color: kpi.accent || '#999' }}>
          {kpi.label}
          {kpi.explain && (
            <Explainer title={kpi.explain.title || kpi.label} formula={kpi.explain.formula} align={kpi.explainAlign || 'left'}>
              {kpi.explain.body}
            </Explainer>
          )}
        </span>
        <div className="kpi-card-icon" style={{ background: kpi.iconBg || 'rgba(27,94,32,0.08)' }}>
          <Icon name={kpi.icon} size={15} color={kpi.accent || '#1B5E20'} />
        </div>
      </div>
      <div className="kpi-card-value" style={kpi.muted ? { color: '#6B7168' } : undefined}>
        {kpi.value}
      </div>
      <div className="kpi-card-sub">{kpi.sub}</div>
      {kpi.trend && <div style={{ fontSize: 10, color: '#43A047', fontWeight: 600, marginTop: 6 }}>{kpi.trend}</div>}
      {(kpi.yearLabel || kpi.footer) && (
        <div className="kpi-card-foot">
          {kpi.yearLabel && <div style={{ fontSize: 9, color: '#8A8A8A', fontWeight: 600 }}>{kpi.yearLabel}</div>}
          {kpi.footer}
        </div>
      )}
    </div>
  );
}
